import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { ApplySortPayload } from 'app/core/shared/component/sort-menu/sort-menu.component';
import { DEFAULT_COLUMN_LABELS } from 'app/core/shared/constant/column-label';
import module from 'app/modules/well-schematic-diagram/well-schematic-diagram.module';
import { Subject, takeUntil, merge, switchMap, EMPTY, map, take } from 'rxjs';
import { FormComponent } from '../../components/form/form.component';
import { WellSchematicApplication, FormTypeResponse } from '../../model';
import { WellSchematicApplicationService } from '../../services/well-schematic-application.service';
import { WellBarrierStatusHistoricalRecordsComponent } from '../../components/well-barrier-status-historical-records/well-barrier-status-historical-records.component';

@Component({
    selector: 'app-well-barrier-status',
    standalone: true,
    imports: [...module],
    templateUrl: './well-barrier-status.page.html',
    styleUrl: './well-barrier-status.page.scss'
})
export class WellBarrierStatusPage {
    @ViewChild(MatPaginator) private _paginator: MatPaginator;
    @ViewChild(MatSort) private _sort: MatSort;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    searchForm: UntypedFormGroup;
    dt: any;
    startingIndex: number = 0;
    isLoading: boolean = false;

    sortInfo: string = 'Created Date';
    private columnLabels: { [key: string]: string } = {
        ...DEFAULT_COLUMN_LABELS,
        status: 'Status',
    };

    columnSort = Object.entries(this.columnLabels).map(([key, name]) => ({
        key,
        name,
        isChecked: false, // default unchecked
    }));

    constructor(
        private service: WellSchematicApplicationService,
        private dialog: MatDialog,
        private router: Router,
        private _formBuilder: UntypedFormBuilder,
        private _changeDetectorRef: ChangeDetectorRef
    ) { }

    ngOnInit() {
        this.setSearch();
        this.setDataTable();
        this.setPage({ offset: 0 });
    }

    ngAfterViewInit() {
        if (this._sort && this._paginator) {
            // Set the initial sort
            this._sort.sort({
                id: 'field',
                start: 'asc',
                disableClear: true,
            });

            // Mark for check
            this._changeDetectorRef.markForCheck();

            // If the user changes the sort order...
            this._sort.sortChange
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    // Reset back to the first page
                    this._paginator.pageIndex = 0;

                    this.onSort(this._sort);
                });

            // Get platform types if sort or page changes
            merge(this._sort.sortChange, this._paginator.page)
                .pipe(
                    switchMap(() => {
                        // this.isLoading = true;

                        this.startingIndex =
                            this._paginator.pageIndex *
                            this._paginator.pageSize;

                        let pageInfo = {
                            count: this._paginator.length,
                            pageSize: this._paginator.pageSize,
                            limit: this._paginator.pageSize,
                            offset: this._paginator.pageIndex,
                        };
                        this.setPage(pageInfo); // Emit page number

                        return EMPTY;
                    }),
                    map(() => {
                        this.isLoading = false;
                    })
                )
                .subscribe();
        }
    }

    onSort(sort: any) {
        if (!sort) return;
        this.searchForm.patchValue({
            OrderBy1: sort.active,
            OrderDir: sort.direction,
        });

        this.sortInfo = this.columnLabels[sort.active] || sort.active;
        this.setPage({ offset: 0 });
    }

    setSearch() {
        this.sortInfo = 'Created Date';
        this.searchForm = this._formBuilder.group({
            Page: [0],
            PageSize: [20],
            OrderBy1: [null],
            OrderBy2: [null],
            OrderBy3: [null],
            OrderDir: 'asc',
            WellName: [''],
            PlatformName: [''],
            StringName: [''],
            FieldName: [''],
        });

        this.searchForm
            .get('PageSize')
            .valueChanges.subscribe((value) => this.setSize(value));
    }

    setSize(size: number) {
        this.dt.page.size = size;
        this.dt.page.offset = 0;
        this.setPage({ offset: this.dt.page.offset });
    }

    setDataTable() {
        this.dt = {
            page: {
                size: this.searchForm.get('PageSize')?.value,
                totalRecords: 0,
                totalPages: 0,
                offset: 0,
            },
            rows: [],
        };
    }

    setPage(pageInfo) {
        this.dt.page.offset = pageInfo.offset;
        this.startingIndex = this.dt.page.offset * this.dt.page.size;

        this.searchForm.patchValue({
            Page: this.dt.page.offset + 1,
        });

        const params = this.searchForm.value;

        const search: any = {
            wellLongName: params.Well?.wellLongName,
            stringShortName: params.String?.stringShortName,
            platformLongName: params.Platform?.platformLongName,
            fieldLongName: params.Field?.fieldLongName,
        };

        this.service
            .getWellApplicationList({ ...params, search })
            .subscribe((pagedData) => {
                let records = pagedData.data.data.filter((item) => item.status == 'Approved');

                if (records.length == 0) return
                this.dt.page.totalPages = Math.ceil(this.dt.page.size / records.length);
                this.dt.page.totalRecords = records.length;
                this.dt.rows = records;
            });
    }

    downloadExcel() {
        this.service.downloadExcel();
    }

    update(row: WellSchematicApplication) {
        const dialogRef = this.dialog.open(FormComponent, {
            data: {
                payload: row,
            },
            width: '70%',
        });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe(
                (response: FormTypeResponse<WellSchematicApplication>) => {
                    if (!response.isUpdate) return;
                    this.service.updateWellApplicationList(response.payload);
                    this.setPage({ offset: this.dt.page.offset });
                }
            );
    }

    toSchematic(row: WellSchematicApplication) {
        const path = `/data-management/well-barrier-status/${row.wellStringId}/details/${row.id}`
        const params: any = {};

        this.router.navigate([path], params);
    }

    reset() {
        this.setSearch();
        this.setPage({ offset: 0 });
    }

    applySort(payload: ApplySortPayload) {
        // Patch form (fill missing with null)
        const [OrderBy1, OrderBy2, OrderBy3] = [
            payload.columns[0]?.key ?? null,
            payload.columns[1]?.key ?? null,
            payload.columns[2]?.key ?? null,
        ];

        this.searchForm.patchValue({
            OrderBy1,
            OrderBy2,
            OrderBy3,
            OrderDir: payload.orderDir ?? 'asc',
        });

        const labels = payload.columns.map(k => k.name);
        this.sortInfo = labels.length ? labels.join(' → ') : 'No sorting';
        this.setPage({ offset: 0 });
    }

    openHistoricalModal(wellDetails) {
        const dialogRef = this.dialog.open(WellBarrierStatusHistoricalRecordsComponent, {
            data: wellDetails,
            width: "99%",
            maxWidth: "98%"
        });

        // dialogRef.afterClosed().subscribe(result => {
        //     console.log(`Dialog result: ${result}`);
        // });
    }

}
